// @greenlight-spec: 28-it-dashboard-ui.md
// Preview features: surfaces whose UI ships ahead of the backing platform
// capability. Each one is a per-browser opt-in flipped from the flask menu
// (PreviewFeaturesMenu.tsx). This registry is the single inventory of
// outstanding gates — when a capability ships for real, delete its key here and
// the compiler flags every stale call site. Only preview-capable builds (the dev
// server, or `VITE_GL_PREVIEW_FEATURES=1` in the dogfood image) resolve flags at
// all: `isPreviewBuild()` const-folds to false in a default production build, so
// customer bundles carry no preview surface.

export function isPreviewBuild(): boolean {
  return import.meta.env.DEV || import.meta.env.VITE_GL_PREVIEW_FEATURES === '1';
}

interface PreviewFeatureDef {
  /** Checkbox label in the flask menu. */
  label: string;
  /** What turning the flag on reveals, and what backend piece is still missing. */
  description: string;
}

export const PREVIEW_FEATURES = {
  'cost-management': {
    label: 'Cost Management page',
    description:
      'The /cost view (docs/11 §Cost Management): what Greenlight charges per app ' +
      'and for the install, and build token cost, on live data. Held back from ' +
      'release builds until Shift releases it.',
  },
  'greenlight-agents': {
    label: 'Greenlight Agents',
    description:
      'The /agents destination (docs/59): the agent catalog, per-agent sessions, ' +
      'the activity feed with action approvals, and inference configuration, on ' +
      'fixture data. No platform agent runtime exists yet.',
  },
} as const satisfies Record<string, PreviewFeatureDef>;

export type PreviewFeature = keyof typeof PREVIEW_FEATURES;

export const PREVIEW_FEATURE_KEYS = Object.keys(PREVIEW_FEATURES) as PreviewFeature[];

const storageKey = (feature: PreviewFeature): string => `gl-preview-${feature}`;

export function isPreviewFeatureEnabled(feature: PreviewFeature): boolean {
  if (!isPreviewBuild()) return false;
  try {
    return window.localStorage.getItem(storageKey(feature)) === '1';
  } catch {
    return false;
  }
}

export function setPreviewFeatureEnabled(feature: PreviewFeature, enabled: boolean): void {
  try {
    if (enabled) window.localStorage.setItem(storageKey(feature), '1');
    else window.localStorage.removeItem(storageKey(feature));
  } catch {
    /* ignore */
  }
}
